import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Users, UserPlus, Shield, User, Hash, Pencil, Clock, CheckCircle, XCircle, Trash2, Target } from "lucide-react";

const API = '/api';

export default function UsersPage() {
  const { getAuthHeader, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingShooters, setPendingShooters] = useState([]);
  const [activeShooters, setActiveShooters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [activeTab, setActiveTab] = useState("approved");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "standard",
    shooter_id: "",
    linked_shooter_id: "",
  });

  useEffect(() => {
    fetchUsers();
    fetchPendingUsers();
    fetchPendingShooters();
    fetchActiveShooters();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users?t=${Date.now()}`, {
        headers: getAuthHeader(),
      });
      // Backend already filters to approved users
      setUsers(res.data || []);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const res = await axios.get(`${API}/users/pending?t=${Date.now()}`, {
        headers: getAuthHeader(),
      });
      setPendingUsers(res.data || []);
    } catch (error) {
      console.error("Failed to fetch pending users");
    }
  };

  const fetchPendingShooters = async () => {
    try {
      const res = await axios.get(`${API}/shooters/pending?t=${Date.now()}`, {
        headers: getAuthHeader(),
      });
      setPendingShooters(res.data || []);
    } catch (error) {
      console.error("Failed to fetch pending shooters");
    }
  };

  const fetchActiveShooters = async () => {
    try {
      const res = await axios.get(`${API}/shooters?t=${Date.now()}`);
      setActiveShooters(res.data || []);
    } catch (error) {
      console.error("Failed to fetch shooters");
    }
  };

  const handleApproveShooter = async (shooterId) => {
    try {
      await axios.post(`${API}/shooters/${shooterId}/approve`, {}, { headers: getAuthHeader() });
      toast.success("Shooter approved");
      fetchPendingShooters();
      fetchActiveShooters();
    } catch (error) {
      toast.error("Failed to approve shooter");
    }
  };

  const handleRejectShooter = async (shooterId) => {
    if (!window.confirm("Reject this shooter application? The shooter record will be deleted.")) return;
    try {
      await axios.post(`${API}/shooters/${shooterId}/reject`, {}, { headers: getAuthHeader() });
      toast.success("Shooter application rejected");
      fetchPendingShooters();
      fetchUsers();
    } catch (error) {
      toast.error("Failed to reject application");
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          role: formData.role,
          shooter_id: formData.shooter_id || null,
          linked_shooter_id: formData.linked_shooter_id || null,
        };
        await axios.put(`${API}/users/${editingUser.id}`, updateData, {
          headers: getAuthHeader(),
        });
        toast.success("User updated");
      } else {
        // Create new user
        await axios.post(`${API}/auth/create-user`, formData, {
          headers: getAuthHeader(),
        });
        toast.success("User created");
      }
      setDialogOpen(false);
      resetForm();
      // Small delay to ensure database transaction completes
      setTimeout(() => {
        fetchUsers();
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || "",
      password: "",
      role: user.role,
      shooter_id: user.shooter_id || "",
      linked_shooter_id: user.linked_shooter_id || "",
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({ name: "", email: "", phone: "", password: "", role: "standard", shooter_id: "", linked_shooter_id: "" });
  };

  const handleApproveUser = async (userId, approved) => {
    try {
      await axios.post(`${API}/users/${userId}/approve`, 
        { status: approved ? "approved" : "rejected" },
        { headers: getAuthHeader() }
      );
      toast.success(approved ? "User approved" : "User rejected");
      fetchPendingUsers();
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (userId === currentUser.id) {
      toast.error("Cannot change your own role");
      return;
    }
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole }, {
        headers: getAuthHeader(),
      });
      toast.success(`User role updated to ${newRole}`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === currentUser.id) {
      toast.error("Cannot delete your own account");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      return;
    }
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: getAuthHeader(),
      });
      toast.success("User deleted");
      setTimeout(() => {
        fetchUsers();
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="users-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            User Management
          </h1>
          <p className="text-slate-500 mt-1">Manage users and approve registrations</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => { resetForm(); setDialogOpen(true); }}
              className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
            >
              <UserPlus size={20} className="mr-2" />
              Create User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                {editingUser ? "Edit User" : "Create New User"}
              </DialogTitle>
              <DialogDescription>
                {editingUser ? "Update user information" : "Create a new user account with specified role"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="shooter_id">Shooter ID</Label>
                <Input
                  id="shooter_id"
                  value={formData.shooter_id}
                  onChange={(e) => setFormData({ ...formData, shooter_id: e.target.value })}
                  placeholder="e.g., 001, A-123"
                />
                <p className="text-xs text-slate-500 mt-1">Unique shooter identifier for results</p>
              </div>
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              {!editingUser && (
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                    minLength={6}
                  />
                </div>
              )}
              <div>
                <Label htmlFor="role">Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(val) => setFormData({ ...formData, role: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard User</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editingUser && (
                <div>
                  <Label htmlFor="linked_shooter">Linked Shooter Profile</Label>
                  <p className="text-xs text-slate-500 mb-1">
                    Link this user to a shooter profile so they appear in score entry and "My Results". Useful when an admin also competes.
                  </p>
                  <Select
                    value={formData.linked_shooter_id || "__none__"}
                    onValueChange={(val) =>
                      setFormData({ ...formData, linked_shooter_id: val === "__none__" ? "" : val })
                    }
                  >
                    <SelectTrigger data-testid="link-shooter-select">
                      <SelectValue placeholder="None (not linked)" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="__none__">— Not linked —</SelectItem>
                      {activeShooters.map((sh) => (
                        <SelectItem key={sh.id} value={sh.id}>
                          {sh.name}{sh.shooter_id ? ` (#${sh.shooter_id})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button type="submit" className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90">
                {editingUser ? "Update User" : "Create User"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs for Approved and Pending Users */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="approved" className="flex items-center gap-2">
            <CheckCircle size={16} />
            Approved Users ({users.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock size={16} />
            Pending Approval ({pendingUsers.length})
            {pendingUsers.length > 0 && (
              <Badge variant="destructive" className="ml-1">{pendingUsers.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="shooter-requests" className="flex items-center gap-2" data-testid="pending-shooters-tab">
            <Target size={16} />
            Shooter Requests ({pendingShooters.length})
            {pendingShooters.length > 0 && (
              <Badge variant="destructive" className="ml-1">{pendingShooters.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="approved">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <Users className="text-[#1B3B6F]" />
                All Approved Users
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Shooter ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} data-testid={`user-row-${user.id}`}>
                      <td className="font-mono text-sm">
                        {user.shooter_id ? (
                          <span className="flex items-center gap-1">
                            <Hash size={12} className="text-slate-400" />
                            {user.shooter_id}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="font-medium">
                        {user.name}
                        {user.id === currentUser.id && (
                          <span className="text-xs text-slate-400 ml-2">(you)</span>
                        )}
                      </td>
                      <td className="text-slate-600">{user.email}</td>
                      <td className="text-slate-500">{user.phone || "-"}</td>
                      <td>
                        <Badge
                          variant={user.role === "admin" ? "default" : "secondary"}
                          className={user.role === "admin" ? "bg-amber-100 text-amber-800" : ""}
                        >
                          {user.role === "admin" ? (
                            <><Shield size={12} className="mr-1" /> Admin</>
                          ) : (
                            <><User size={12} className="mr-1" /> Standard</>
                          )}
                        </Badge>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(user)}
                          >
                            <Pencil size={14} />
                          </Button>
                          {user.id !== currentUser.id && (
                            <>
                              <Select
                                value={user.role}
                                onValueChange={(val) => handleRoleChange(user.id, val)}
                              >
                                <SelectTrigger className="w-28">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="standard">Standard</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </>
                          )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="pending">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <Clock className="text-amber-600" />
                Pending Registrations
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {pendingUsers.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  No pending registrations
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Registered</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingUsers.map((user) => (
                      <tr key={user.id}>
                        <td className="font-medium">{user.name}</td>
                        <td className="text-slate-600">{user.email}</td>
                        <td className="text-slate-500">{user.phone || "-"}</td>
                        <td className="text-slate-500 text-sm">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApproveUser(user.id, true)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleApproveUser(user.id, false)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <XCircle size={14} className="mr-1" />
                              Reject
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="shooter-requests">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <Target className="text-amber-600" />
                Pending Shooter Requests
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {pendingShooters.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  No pending shooter applications
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Requesting User</th>
                      <th>Shooter ID #</th>
                      <th>Phone</th>
                      <th>Requested</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingShooters.map((sh) => (
                      <tr key={sh.id} data-testid={`pending-shooter-row-${sh.id}`}>
                        <td className="font-medium">{sh.name}</td>
                        <td className="text-slate-600 text-sm">
                          {sh.requesting_user_name || sh.requesting_user_username || "-"}
                          {sh.requesting_user_email && (
                            <div className="text-xs text-slate-400">{sh.requesting_user_email}</div>
                          )}
                        </td>
                        <td className="text-slate-500 font-mono text-sm">{sh.shooter_id || "-"}</td>
                        <td className="text-slate-500">{sh.phone || "-"}</td>
                        <td className="text-slate-500 text-sm">
                          {sh.created_at ? new Date(sh.created_at).toLocaleDateString() : "-"}
                        </td>
                        <td>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApproveShooter(sh.id)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`approve-shooter-${sh.id}`}
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRejectShooter(sh.id)}
                              className="text-red-600 hover:bg-red-50"
                              data-testid={`reject-shooter-${sh.id}`}
                            >
                              <XCircle size={14} className="mr-1" />
                              Reject
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Info */}
      <div className="text-sm text-slate-500 text-center">
        <p>Admins can access Score Entry, Payments, Sponsors, Reports, Settings, and User Management.</p>
        <p>Standard users can view Today's Events, Matches, Schedules, Announcements, Event Leaderboards, and their own results.</p>
      </div>
    </div>
  );
}
